import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetDocumentImporterTemplates } from 'src/hooks/document-importer-template/get-document-importer-templates.hook';
import { useDeleteDocumentImporterTemplateById } from 'src/hooks/document-importer-template/delete-document-importer-template-by-id.hook';

import PermissionCheck from 'src/components/permission-check';
import { DrawerComponent } from 'src/components/custom-drawer/';
import { ChangeDocumentImportPattern } from './components/change-document-import-pattern';
import { DocumentImportPatternsTable } from './components/document-import-patterns-table';

import {
  DOCUMENT_PATTERN_TYPE,
  DOCUMENT_PATTERN_MODALS,
} from './document-import-patterns.constants';

import { DocumentImportWrapper } from './document-import-patterns.styles';

const DocumentImportPatterns = () => {
  const { t } = useTranslation('settings');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [documentPattern, setDocumentPattern] = useState(DOCUMENT_PATTERN_TYPE);

  const currentCompanyId = useGetCurrentCompanyId();
  const { collection, paginationConfig, onTableChange, loading } = useGetDocumentImporterTemplates(
    currentCompanyId
  );

  const handleCreatePattern = () => {
    setActiveModal(DOCUMENT_PATTERN_MODALS.create);
  };

  const closeModal = () => {
    setActiveModal(null);
    setDocumentPattern(DOCUMENT_PATTERN_TYPE);
  };

  const deleteDocumentImporterTemplateById = useDeleteDocumentImporterTemplateById({
    onSuccess: closeModal,
  });

  const deletePattern = () => {
    deleteDocumentImporterTemplateById({
      variables: { id: documentPattern?._id },
    });
    setDocumentPattern(DOCUMENT_PATTERN_TYPE);
    closeModal();
  };

  const onSubmitDelete = () => {
    deletePattern();
  };

  const isCreate = activeModal === DOCUMENT_PATTERN_MODALS.create;
  const isEdit = activeModal === DOCUMENT_PATTERN_MODALS.edit;

  return (
    <DocumentImportWrapper>
      <DrawerComponent
        title={t(
          `documentImporter.labels.${isCreate ? 'addNewImportPattern' : 'editImportPattern'}`
        )}
        onClose={closeModal}
        visible={isEdit || isCreate}
        destroyOnClose
      >
        <ChangeDocumentImportPattern onClose={closeModal} id={documentPattern._id} />
      </DrawerComponent>
      <DeleteModal
        translate={t}
        isOpened={activeModal === DOCUMENT_PATTERN_MODALS.delete}
        title={t('documentImporter.deleteModal.title')}
        subTitle={t('documentImporter.deleteModal.subtitle', {
          name: documentPattern.title,
        })}
        onClose={closeModal}
        onSubmit={onSubmitDelete}
      />
      <div>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('documentImporter.labels.documentImportPatterns')}
        </Typography>
        <PermissionCheck
          permission={PLATFORM_PERMISSIONS.documentImporter}
          action={PERMISSION_ACTIONS.create}
        >
          <ButtonsWrapper>
            <Button
              type="primary"
              ghost
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={handleCreatePattern}
            >
              {t('documentImporter.buttons.addImportPattern')}
            </Button>
          </ButtonsWrapper>
        </PermissionCheck>
      </div>
      <DocumentImportPatternsTable
        dataSource={collection}
        paginationConfig={paginationConfig}
        onTableChange={onTableChange}
        handleModals={(modal, record) => {
          setActiveModal(modal);
          setDocumentPattern(record);
        }}
        loading={loading}
      />
    </DocumentImportWrapper>
  );
};

export default DocumentImportPatterns;
